import React, { useEffect, useState, useContext, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import Button from "react-bootstrap/Button";

import { loginRequest } from "../authConfig";
import { downloadExportFile, getMyDirectReports} from "../graph";
import { Context } from "../Context.js";
import { humanizeDate } from "../helper/date";
import debounce from "../helper/debounce";

import "../styles/DirectReports.css";
import { ModalViewDetailsOffboard } from "./ModalViewDetailsOffboard";

/**
 * Renders information about the user obtained from MS Graph
 * @param props 
 */
export const DirectReports = (props) => {
    const { instance, accounts } = useMsal();
    const [ directReports, setDirectReports ] = useState(props.directReports);
    const [employeeSelected, setEmployeeSelected] = useState(null);
    const [ userToSearch, setUserToSearch ] = useState('');
    const [, setIsLoading] = useContext(Context);

    useEffect(() => {
        if (!userToSearch) {
            setDirectReports(props.directReports);
        } else {
            setIsLoading(true);
            // Silently acquires an access token which is then attached to a request for MS Graph data
            instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            }).then((response) => {
                getMyDirectReports(response.accessToken, '', userToSearch).then(directReports => {
                    setDirectReports(directReports.users);
                    setIsLoading(false);
                });
            });
        }
    }, [props.directReports, userToSearch]);

    const rowClicked = (user) => {
        setEmployeeSelected(user);
    };

    const loadNextPage = () => {
        props.onLoadNextPage(props.loadMoreLink);
    }

    const exportExcel = () => {
        setIsLoading(true);
        // Silently acquires an access token which is then attached to a request for MS Graph data
        instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        }).then((response) => {
            downloadExportFile(response.accessToken)
                .then((response) => response.blob())
                .then((blob) => {
                    // It is necessary to create a new blob object with mime-type explicitly set
                    // otherwise only Chrome works like it should
                    const newBlob = new Blob([blob], { type: blob.type });
                    const fileName = `ASM_export_data_${new Date().toISOString()}.xlsx`;

                    // IE doesn't allow using a blob object directly as link href
                    // instead it is necessary to use msSaveOrOpenBlob
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(newBlob, fileName);
                        return;
                    }

                    // For other browsers:
                    // Create a link pointing to the ObjectURL containing the blob.
                    const data = window.URL.createObjectURL(newBlob);
                    const link = document.createElement('a');
                    link.href = data;
                    link.download = fileName;
                    link.click();

                    // For Firefox it is necessary to delay revoking the ObjectURL
                    setTimeout(() => { window.URL.revokeObjectURL(data); setIsLoading(false) }, 100);
                });
        });
    };

    const onUpdateEmployee = (employee) => {
        const updatedDirectReport = directReports.map((d) => d.id === employee.id ? employee : d);
        setDirectReports(updatedDirectReport);
    };

    const search = (event) => {
        setUserToSearch(event.target.value);
    };

    const debouncedSearch = useMemo(() => debounce(search, 300), []);

    const isAdmin = () => ['Operational Technology', 'People and Culture'].includes(props.department);

    return directReports && (
        directReports.length > 0 || userToSearch ? (
            <>
                <div className="directReports">
                    <div className="title">
                        <span>{isAdmin() ? 'ASM Employees' : 'Direct reports'}</span>
                        {isAdmin() && (<input onChange={debouncedSearch} type="text" className="searchUsers" placeholder="Search user..." /> )}
                        <Button variant="warning" className="ml-auto tablet-up" onClick={exportExcel}>{isAdmin() ? 'Export all' : 'Export direct reports'}</Button>
                    </div>
                    <div className="content">
                        <div className="header">
                            <span>Employee</span>
                            <span className="desktop-up">Position</span>
                            <span className="desktop-up">Department</span>
                            <span>Manager</span>
                            <span className="desktop-up">Home Org</span>
                            <span>Leaving Date</span>
                            <span className="tablet-up">Reason of Leaving</span>
                            <span className="phoneBig-up">Status</span>
                        </div>
                        { directReports && directReports.map((directReport) => {
                            const status = directReport.exts5qeux5w_WiderTeam && directReport.exts5qeux5w_WiderTeam.accountStatus && directReport.exts5qeux5w_WiderTeam.accountStatus === 'active' ? 'active' : 'inactive';
                            
                            return (
                                <div key={directReport.id} className="employee" onClick={() => rowClicked(directReport)}>
                                    <span>{directReport.givenName} {directReport.surname}</span>
                                    <span className="desktop-up">{directReport.jobTitle}</span>
                                    <span className="desktop-up">{directReport.department}</span>
                                    <span>{directReport.managerName}</span>
                                    <span className="desktop-up">{directReport.exts5qeux5w_WiderTeam && directReport.exts5qeux5w_WiderTeam.HomeOrg ? directReport.exts5qeux5w_WiderTeam.HomeOrg : ''}</span>
                                    <span>{directReport.exts5qeux5w_WiderTeam && directReport.exts5qeux5w_WiderTeam.terminationDate ? humanizeDate(directReport.exts5qeux5w_WiderTeam.terminationDate) : ''}</span>
                                    <span className="tablet-up">{directReport.exts5qeux5w_WiderTeam && directReport.exts5qeux5w_WiderTeam.terminationReasonDescription ? directReport.exts5qeux5w_WiderTeam.terminationReasonDescription : ''}</span>
                                    <span className={`phoneBig-up employeeStatus status-${status}`}>{status}</span>
                                </div>
                            )
                        })}
                        {props.loadMoreLink && !userToSearch && (
                            <Button variant="dark" className="loadMoreButton" onClick={loadNextPage}>Load more...</Button>
                        )}
                    </div>
                </div>
                <ModalViewDetailsOffboard user={employeeSelected} editable={isAdmin()} onClose={() => setEmployeeSelected(null)} onUpdateEmployee={onUpdateEmployee}/>
            </>
        ) : (
            <div className="directReports none">
                <div className="title">
                    <span>Direct reports</span>
                </div>
                <div className="content">
                    <span>There is no one reporting to you at the moment</span>
                </div>
            </div>
        )
    );
};